import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Select from 'react-select';
import Typography from '@material-ui/core/Typography';
import intl from 'react-intl-universal';
import 'react-select/dist/react-select.css';
import styled from 'src/ui/library/styled';

const ITEM_HEIGHT = 48;

const styles = theme => ({
  root: {
    position: 'relative',

    backgroundColor: theme.colors.eyGrey5,
    maxWidth: '300px',
    border: '1px solid transparent',
  },
  enableAutocompleteField: {
    borderBottom: '1px solid #2E2E38',
    "&:hover, &:focus-within":{
      background: '#E1E1E6 !important',
    }
  },
  labelPlaceholder: {
    height: '16px',
  },
  floatLabel: {
    position: 'absolute',
    top: '0',
    left: '0',
    margin: '0',
    color: 'black',
    fontSize: '1rem',
    transform: 'translate(0, 18px) scale(1)',
    transition: 'transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
    width: 'calc(100% - 32px)',
  },
  floatLabelShrink: {
    margin: '0',
    transformOrigin: 'top left',
    left: '0',
    transform: 'translate(0, 8px)',
    fontSize: '0.8rem',
    color: '#747480',
  },
  labelAsterisk: {
    color: 'red',
  },
  floatLabeldisable: {
    margin: '0',
    color: '#aa9fa2',
  },

  '@global': {
    '.Select-loading-zone': {
      display: 'none', // this is causing some UI issues. hiding for now
    },
    '.Select-control': {
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      background: 'transparent',
      '&:hover': {
        boxShadow: 'none',
      },
    },
    '.Select-multi-value-wrapper': {
      flexGrow: 1,
      display: 'flex',
      flexWrap: 'wrap',
    },
    '.Select--multi .Select-input': {
      margin: 0,
    },
    '.Select--multi .Select-value': {
      margin: '5px 5px 0px 0px',
    },

    '.Select-noresults': {
      padding: theme.spacing.unit * 2,
    },
    '.Select-input': {
      display: 'inline-flex !important',
      padding: 0,
    },
    '.Select-input input': {
      background: 'transparent',
      border: 0,
      padding: 0,
      cursor: 'default',
      display: 'inline-block',
      fontFamily: 'inherit',
      fontSize: 'inherit',
      margin: 0,
      outline: 0,
    },
    '.Select-placeholder': {
      display: 'none',
    },
    '.Select-menu-outer': {
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[2],
      position: 'absolute',
      left: 0,
      top: `calc(100%)`,
      width: '100%',
      zIndex: 1000,
      maxHeight: ITEM_HEIGHT * 4.5,
    },
    '.Select.is-focused:not(.is-open) > .Select-control': {
      boxShadow: 'none',
    },
    '.Select-option.is-focused': {
      backgroundColor: theme.colors.eyGrey4,
    },
    '.Select-menu': {
      maxHeight: ITEM_HEIGHT * 4.5,
      overflow: 'hidden',
      overflowY: 'scroll',
    },
    '.Select-menu div': {
      boxSizing: 'content-box',
      height: 'auto',
      overflow: 'hidden',
      //   whiteSpace: 'nowrap',
      fontSize: '14px',
      //   textOverflow: 'ellipsis',
      '&:hover': {
        backgroundColor: theme.colors.eyGrey4,
      },
    },
    '.Select-clear-zone': {
      display: 'none',
    },
    '.Select-value': {
      background: 'none !important',
      border: 'none !important',
      '&:after': {
        content: '";" !important',
      },
    },
    '.Select-value-icon': {
      display: 'none !important',
    },
    '.Select-value-label': {
      padding: '2px 0px 2px 0px !important',
    },
    '.Select.has-value.is-disabled.Select--single > .Select-control .Select-value': {
      color: '#aa9fa2',
    },
  },
});

class AutocompleteBase extends React.Component {
  static propTypes = {
    /** Pre-defined custom styles to override AutocompleteBase */
    styleType: PropTypes.string,
    /** Default value to seed uncontrolled component */
    defaultValue: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    /** Floating label */
    label: PropTypes.string.isRequired,
    /** Add custom footer after AutoComplete */
    footer: PropTypes.func,
    /** Async function used to load in AutoComplete data */
    loadOptions: PropTypes.func.isRequired,
    /** Function to override default filtering */
    onFilter: PropTypes.func,
    /** Function to override default option rendering */
    renderOption: PropTypes.func,
    /** Function to override default value rendering */
    renderValue: PropTypes.func,
    /** Callback function that fires when autocomplete adds/removes values */
    onChange: PropTypes.func.isRequired,
    /** Function to override default arrowRenderer */
    renderIcon: PropTypes.func,

    setExactInput: PropTypes.func,
  };

  static defaultProps = {
    footer: null,
    onFilter: null,
    onChange: null,
    renderOption: null,
    renderValue: null,
    renderIcon: null,
  };

  constructor(props) {
    super(props);
    let modifiedState = {};

    if (props.defaultValue) {
      modifiedState = { value: props.defaultValue };
    }

    this.state = {
      isFocused: false,
      value: null,
      valueObject: {},
      searchText: null,
      isMenuOpen: false,
      ...modifiedState,
    };
  }
  componentDidUpdate(prevProps, prevState) {
    if (this.state.value === null && this.props.defaultValue !== this.state.value) {
      //this.props.defaultValue, prevState.value
      if (prevState.value === null) {
        this.setState({ value: this.props.defaultValue });
      } else if (prevState.value[this.props.valueKey] !== this.props.defaultValue[this.props.valueKey]) {
        this.setState({ value: this.props.defaultValue });
      }
    }
  }

  toggleFocus = val => {
    this.setState({
      isFocused: val,
    });
  };

  toggleMenu = status => {
    this.setState(
      {
        isMenuOpen: status,
      },
      () => {
        if (this.props.onMenuChange) this.props.onMenuChange(this.state.isMenuOpen);
      },
    );
  };

  handleLabelClick = () => {
    this.selectRef.focus();
  };

  handleKeyDown = (searchText, action) => {
    this.setState({ searchText });
    this.props.setExactInput(searchText);
  };

  handleChange = value => {
    let lKey = this.props.labelKey;
    let valueObj = {};
    if (value) {
      valueObj[lKey] = value[lKey];
    } else {
      valueObj = null;
    }

    this.setState({ ...this.state, valueObject: value, value: valueObj }, () => {
      if (this.props.onChange) this.props.onChange(this.state.value, this.state.valueObject);
    });
  };

  render() {
    const {
      classes,
      data,
      defaultValue,
      label,
      onFilter,
      renderOption,
      renderValue,
      onChange,
      footer,
      renderIcon,
      children,
      styleType,
      hideIconOnSelect,
      valueKey,
      labelKey,
      required,
      ...otherProps
    } = this.props;

    const { isFocused, searchText, isMenuOpen, value } = this.state;
    const rootClass = classNames({[classes.root]: true, 
                                [classes.enableAutocompleteField]: !this.props.disabled,
                                styleType: styleType,
                                });

    return (
      <React.Fragment>
        <div className={rootClass} style={{ background: '#F6F5FA' }}>
          <div className={classes.labelPlaceholder} />
          <Select.Async
            multi={true}
            label={''}
            cache={false}
            ref={el => (this.selectRef = el)}
            noResultsText={<Typography>{intl.get('autocomplete.noResults')}</Typography>}
            onInputChange={this.handleKeyDown}
            onBlur={this.toggleFocus.bind(null, false)}
            onFocus={this.toggleFocus.bind(null, true)}
            onChange={this.handleChange}
            filterOption={onFilter}
            backspaceRemoves={true}
            value={value}
            optionRenderer={data => renderOption(data, searchText, labelKey)}
            valueComponent={conf => renderValue(conf, labelKey, this.props.disabled)}
            arrowRenderer={(hideIconOnSelect && value === null) || !hideIconOnSelect ? renderIcon : null}
            {...otherProps}
          />
          <label
            onClick={this.handleLabelClick}
            className={classNames({
              [classes.floatLabel]: true,
              [classes.floatLabeldisable]: this.props.disabled,
              [classes.floatLabelShrink]: isFocused || value !== null ? true : false,
            })}>
            {label}
            {required && 
              intl.get('questionnaire.requiredLabel')
              // <span className={classes.labelAsterisk}> *</span>
            }
          </label>
        </div>
        {footer && footer({ value, searchText, isFocused, isMenuOpen })}
      </React.Fragment>
    );
  }
}

export { AutocompleteBase };
export default styled(AutocompleteBase)(styles);
